/* eslint-disable jsdoc/check-param-names */
import React from 'react';
import { registerBrick, Auth, BrickContext } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import { Typography, Button, Menu, MenuItem, ThemeProvider } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { buttonsTheme } from './_theme.jsx';
import { UserAvatar } from './UserAvatar.jsx';
import { regionLabels } from './_enums';

export default class Header extends ReactBrick {
    /**
     * Colored Tile that changes color while hovered
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array<*>} properties
     * @returns {Element}
     */
    static getReactComponent($) {
        return (props) => {
            const screenTitle = useProperty($, 'Screen Title');
            const defaultColor = useProperty($, 'Default Color');

            // Help Menu
            const [helpAnchorEl, setHelpAnchorEl] = React.useState(null);
            const helpOpen = Boolean(helpAnchorEl);
            const handleHelpClick = (event) => {
                setHelpAnchorEl(event.currentTarget);
            };
            const handleHelpClose = () => {
                setHelpAnchorEl(null);
            };
            const handleReportIssueClick = () => {
                $.trigger('On Report Issue Click');
                handleHelpClose();
            };
            const handleDocumentationClick = () => {
                $.trigger('On Documentation Click');
                handleHelpClose();
            };

            const helpMenu = (
                <ThemeProvider theme={buttonsTheme}>
                    <Button
                        id="help-button"
                        variant="secondary"
                        className="header-menu-button"
                        aria-controls={open ? 'help-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleHelpClick}
                        startIcon={<HelpOutlineRoundedIcon />}
                        endIcon={helpOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    >
                        Help
                    </Button>
                    <Menu
                        id="help-menu"
                        anchorEl={helpAnchorEl}
                        open={helpOpen}
                        onClose={handleHelpClose}
                        MenuListProps={{
                            'aria-labelledby': 'help-button',
                        }}
                    >
                        <MenuItem onClick={handleReportIssueClick}>Report Issue</MenuItem>
                        <MenuItem onClick={handleDocumentationClick}>Documentation</MenuItem>
                    </Menu>
                </ThemeProvider>
            );

            // User Menu
            const [userAnchorEl, setUserAnchorEl] = React.useState(null);
            const userOpen = Boolean(userAnchorEl);
            const handleUserClick = (event) => {
                setUserAnchorEl(event.currentTarget);
            };
            const handleUserClose = () => {
                setUserAnchorEl(null);
            };
            const handleLogoutClick = () => {
                Auth.logout()
                    .catch((error) => {
                        console.error('Error when logging out:\n', error);
                    })
                    .finally(() => {
                        // Always trigger transition to login screen
                        $.trigger('On Logout Click');
                        handleUserClose();
                    });
            };

            const username = localStorage.getItem('display_username');
            const regionCode = localStorage.getItem('regionCode');
            const userMenu = (
                <ThemeProvider theme={buttonsTheme}>
                    <Button
                        id="user-button"
                        variant="primary"
                        className="header-menu-button"
                        aria-controls={open ? 'user-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleUserClick}
                        endIcon={userOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    >
                        <UserAvatar username={username} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                            <Typography variant="body1">{username}</Typography>
                            <Typography variant="caption">{`Connected to ${regionLabels[regionCode]}`}</Typography>
                        </div>
                    </Button>
                    <Menu
                        id="user-menu"
                        anchorEl={userAnchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        open={userOpen}
                        onClose={handleUserClose}
                        MenuListProps={{
                            'aria-labelledby': 'user-button',
                        }}
                        PaperProps={{
                            style: {
                                width: '200px',
                            },
                        }}
                    >
                        <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
                    </Menu>
                </ThemeProvider>
            );

            return (
                <div
                    className="header-container"
                    style={{ backgroundColor: defaultColor ? defaultColor.toHexString() : undefined }}
                >
                    <Typography className="header-screen-title">{screenTitle}</Typography>
                    {helpMenu}
                    {userMenu}
                </div>
            );
        };
    }
}

registerBrick('0184e2ffe7d215c377a4', Header);
